import { useCallback, useEffect, useState } from "react";
import { LogOut, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "./api";
import { ApplicationCard, STATUSES } from "./ApplicationCard";
import { LogoMark, Wordmark } from "../site/Logo";

export const AdminInbox = ({ user, onLogout }) => {
  const [apps, setApps] = useState([]);
  const [filter, setFilter] = useState("new");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/applications");
      setApps(Array.isArray(data) ? data : []);
    } catch (err) {
      if (err?.response?.status === 401) onLogout();
      else toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/applications/${id}`, { status });
      setApps((list) => list.map((a) => (a.id === id ? { ...a, status } : a)));
      toast.success(`Marked as ${status}`);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this application permanently?")) return;
    try {
      await api.delete(`/admin/applications/${id}`);
      setApps((list) => list.filter((a) => a.id !== id));
      toast.success("Application deleted");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => null);
    onLogout();
  };

  const counts = Object.fromEntries(["all", ...STATUSES].map((s) => [s, s === "all" ? apps.length : apps.filter((a) => a.status === s).length]));
  const shown = filter === "all" ? apps : apps.filter((a) => a.status === filter);

  return (
    <div className="min-h-screen bg-[#E4E4E4] font-body text-black" data-testid="admin-inbox">
      <header className="border-b-2 border-black bg-[#E4E4E4]">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-5 py-4">
          <a href="/" className="flex items-center gap-3" data-testid="admin-home-link">
            <LogoMark className="h-12 w-12" />
            <Wordmark className="hidden h-12 sm:block" fg="#111111" knock="#E4E4E4" />
          </a>
          <div className="flex items-center gap-3">
            <span className="hidden text-sm text-neutral-700 md:inline" data-testid="admin-user-email">{user?.email}</span>
            <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 rounded-full bg-black px-4 py-2 text-sm font-bold text-[#E4E4E4]"><LogOut className="h-4 w-4" /> Log out</button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-5xl px-5 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-4xl font-black sm:text-5xl">Applications</h1>
            <p className="mt-1 text-neutral-700">Service providers who applied through the website.</p>
          </div>
          <button onClick={load} data-testid="admin-refresh" className="inline-flex items-center gap-2 rounded-full border-2 border-black px-4 py-2 text-sm font-bold hover:bg-black hover:text-white"><RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh</button>
        </div>
        <div className="mt-8 flex flex-wrap gap-2" role="tablist">
          {["new", "contacted", "approved", "archived", "all"].map((s) => (
            <button key={s} onClick={() => setFilter(s)} data-testid={`admin-filter-${s}`} className={`rounded-full border-2 border-black px-4 py-2 text-sm font-bold capitalize transition-colors ${filter === s ? "bg-black text-[#E4E4E4]" : "bg-white text-black hover:bg-neutral-200"}`}>
              {s} <span className="ml-1 opacity-70">{counts[s]}</span>
            </button>
          ))}
        </div>
        <div className="mt-8 space-y-6">
          {!loading && shown.length === 0 && (
            <p className="rounded-[22px] border-2 border-dashed border-black/40 p-10 text-center text-neutral-700" data-testid="admin-empty">No {filter === "all" ? "" : filter} applications yet.</p>
          )}
          {shown.map((a) => <ApplicationCard key={a.id} app={a} onStatus={setStatus} onDelete={remove} />)}
        </div>
      </main>
    </div>
  );
};
